import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { BsChevronDown } from "react-icons/bs";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { useToast } from "../../common/hooks/useToast";
import { Popover } from "../../common/popover/Popover";
import { Item } from "../../styles/common";
import { OtpUnblockPanel } from "./OtpUnblockPanel";
import { RateLimitSettingRow } from "./RateLimitSettingRow";
import { RATE_LIMIT_STATUS } from "./rateLimits.queries";
import { UPDATE_RATE_LIMIT_SETTING } from "./rateLimits.mutation";
import {
  ENABLED_KEY,
  REGISTRATION_DEVICE_KEY,
  REGISTRATION_IP_KEY,
  REGISTRATION_OVERRIDE_HINT,
  RateLimitStatus,
  UNWRITABLE_KEYS,
  describeError,
  describeLimitValue,
  dimensionLabel,
  formatRetryAfter,
  getRateLimitRejection,
  groupSettingsByTier,
  parseTierKey,
  sourceMeta,
} from "./rateLimit";

export const RateLimits: React.FC = (): JSX.Element => {
  /**
   * Loaded once on mount and then only on the explicit Refresh button.
   *
   * There is deliberately no `pollInterval`: reads of this screen are metered
   * by the `admin-export` tier at 5/min, 30/hour, 100/day per admin, so a timer
   * would rate-limit the admin out of their own dashboard.
   */
  const { data, loading, error, refetch } = useQuery(RATE_LIMIT_STATUS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [updateSetting] = useMutation(UPDATE_RATE_LIMIT_SETTING);

  const toast = useToast();

  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);

  /**
   * What we last successfully wrote for the master switch. Preferred over the
   * server's `enabled` for display because the server keeps reporting the old
   * value for up to 30 seconds, which would make the toggle snap back.
   */
  const [enabledOverride, setEnabledOverride] = useState<boolean | null>(null);

  const status: RateLimitStatus | undefined = data?.rateLimitStatus;
  const settings = useMemo(() => status?.settings ?? [], [status]);

  const settingByKey = useMemo(
    () => new Map(settings.map((setting) => [setting.key, setting])),
    [settings]
  );

  const advancedGroups = useMemo(
    () => groupSettingsByTier(settings, ["registration"]),
    [settings]
  );

  /**
   * Saves one setting. Never re-reads afterwards: each server instance caches
   * independently for up to 30 seconds, so an immediate read-back would return
   * the old value and look like the save silently failed.
   */
  const handleSave = useCallback(
    async (key: string, value: string): Promise<boolean> => {
      // Backstop for the keys that silently override the rows this screen
      // shows while staying invisible to it — writing one would make the page
      // misreport what is actually enforced. See UNWRITABLE_KEYS.
      if (UNWRITABLE_KEYS.includes(key)) {
        toast.error(`${key} cannot be managed from this panel.`);
        return false;
      }

      setSavingKey(key);

      try {
        const result = await updateSetting({ variables: { key, value } });

        if (result.data?.updateRateLimitSetting !== true) {
          toast.error("The server rejected that change.");
          return false;
        }

        toast.success("Saved. Applied within 30 seconds.");
        return true;
      } catch (mutationError) {
        // A rate-limit rejection is announced by the global Apollo error link
        // already, in the same words; toasting it again here would double it.
        if (!getRateLimitRejection(mutationError)) {
          toast.error(describeError(mutationError, "Could not save the limit"));
        }

        return false;
      } finally {
        setSavingKey(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateSetting]
  );

  const setEnabled = useCallback(
    async (next: boolean) => {
      const saved = await handleSave(ENABLED_KEY, next ? "true" : "false");

      if (saved) setEnabledOverride(next);
    },
    [handleSave]
  );

  const queryRejection = getRateLimitRejection(error);

  if (loading && !data) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading rate limits…
        </Typography>
      </Stack>
    );
  }

  if (queryRejection) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Item>
          <Alert severity="warning" sx={{ m: 2 }}>
            <AlertTitle>This panel hit its own rate limit</AlertTitle>
            {queryRejection.message}
            {queryRejection.retryAfterSeconds !== undefined && (
              <> Try again in {formatRetryAfter(queryRejection.retryAfterSeconds)}.</>
            )}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            </Box>
          </Alert>
        </Item>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Item>
          <ErrorNotifier error={error} />
        </Item>
      </Box>
    );
  }

  if (!status) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
        No rate limit configuration returned.
      </Typography>
    );
  }

  const enabled = enabledOverride ?? status.enabled;
  const enabledPending =
    enabledOverride !== null && enabledOverride !== status.enabled;

  const registrationIp = settingByKey.get(REGISTRATION_IP_KEY);
  const registrationDevice = settingByKey.get(REGISTRATION_DEVICE_KEY);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        {/* ---------------------------------------------- Header */}
        <Grid item xs={12}>
          <Item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={{ m: 2 }} variant="h4">
                Rate Limits
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ m: 2 }}
              >
                <Tooltip title="Auto-refresh is off on purpose: reads of this screen are themselves rate limited (5/min, 30/hour, 100/day per admin).">
                  <Typography variant="caption" color="text.secondary">
                    Loaded on open — refresh manually
                  </Typography>
                </Tooltip>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  onClick={() => refetch()}
                >
                  {loading ? "Refreshing…" : "Refresh"}
                </Button>
              </Stack>
            </Grid>
          </Item>
        </Grid>

        {/* ---------------------------------------------- Status banners */}
        {!enabled && (
          <Grid item xs={12}>
            <Alert severity="error">
              <AlertTitle>Rate limiting is OFF</AlertTitle>
              No limits are being enforced anywhere in the app — registration,
              OTP and everything else are uncapped. Turn the master switch back
              on as soon as the incident is over.
            </Alert>
          </Grid>
        )}

        {!status.redisHealthy && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Redis is unreachable — counting is degraded</AlertTitle>
              Limits have fallen back to per-instance in-memory counters. They
              still apply, but each server counts on its own, so the effective
              limit is looser than configured — roughly multiplied by the number
              of running instances.
            </Alert>
          </Grid>
        )}

        {/* ---------------------------------------------- Registration */}
        <Grid item xs={12}>
          <Item sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Registration limits
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Caps on how many accounts can be created from one source. This is
              the main defence against bulk signup abuse.
            </Typography>

            {/*
             * Stated up front because it cannot be detected: the legacy keys
             * are absent from `rateLimitStatus` and leave the source chip
             * reading `appConfig`, so an overridden row is indistinguishable
             * from a normal one until a save fails to stick.
             */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              These two rows can be overridden by legacy <code>registration.*</code>{" "}
              keys that this panel cannot read. If a saved value comes back
              unchanged after a refresh, that is why — see the warning on the row.
            </Typography>

            <Divider />

            {registrationIp ? (
              <RateLimitSettingRow
                setting={registrationIp}
                label="Max signups per IP address"
                description="Applies to the network address the signup request came from."
                overrideHint={REGISTRATION_OVERRIDE_HINT}
                saving={savingKey === registrationIp.key}
                onSave={handleSave}
              />
            ) : (
              <Alert severity="warning" sx={{ my: 2 }}>
                The server did not return <code>{REGISTRATION_IP_KEY}</code>.
              </Alert>
            )}

            <Divider />

            {registrationDevice ? (
              <RateLimitSettingRow
                setting={registrationDevice}
                label="Max signups per device"
                description="Saves correctly and will take effect the moment the mobile app ships the x-device-id header."
                overrideHint={REGISTRATION_OVERRIDE_HINT}
                badge={
                  <Tooltip title="The mobile app does not send the x-device-id header yet, so this rule is skipped at runtime.">
                    <Chip
                      size="small"
                      color="default"
                      variant="outlined"
                      label="Not enforced yet"
                    />
                  </Tooltip>
                }
                saving={savingKey === registrationDevice.key}
                onSave={handleSave}
              />
            ) : (
              <Alert severity="warning" sx={{ my: 2 }}>
                The server did not return <code>{REGISTRATION_DEVICE_KEY}</code>.
              </Alert>
            )}
          </Item>
        </Grid>

        {/* ---------------------------------------------- Master switch */}
        <Grid item xs={12} md={6}>
          <Item sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Master switch
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The incident kill switch. Turning this off disables every limit in
              the table below.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  disabled={savingKey === ENABLED_KEY}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setEnabled(true);
                      return;
                    }

                    setConfirmDisable(true);
                  }}
                />
              }
              label={enabled ? "Rate limiting enabled" : "Rate limiting DISABLED"}
            />

            {enabledPending && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Saved — applied within 30 seconds.
              </Typography>
            )}
          </Item>
        </Grid>

        {/* ---------------------------------------------- Health */}
        <Grid item xs={12} md={6}>
          <Item sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Counter health
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Where the counters live right now.
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip
                label={status.redisHealthy ? "Redis healthy" : "Degraded"}
                color={status.redisHealthy ? "success" : "warning"}
              />
              <Typography variant="body2" color="text.secondary">
                {status.redisHealthy
                  ? "Counters are shared across all server instances."
                  : "Counting per instance, in memory. Limits are looser than configured."}
              </Typography>
            </Stack>
          </Item>
        </Grid>

        {/* ---------------------------------------------- Unblock */}
        <Grid item xs={12}>
          <OtpUnblockPanel />
        </Grid>

        {/* ---------------------------------------------- Advanced */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<BsChevronDown />}>
              <Typography variant="h6">
                Advanced limits ({advancedGroups.length} tiers)
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every other limit the server enforces. The{" "}
                <strong>Admin export</strong> tier below also governs this
                screen's own calls — lowering it can lock you out of this page.
              </Typography>

              {advancedGroups.map((group) => (
                <Accordion key={group.tier} disableGutters>
                  <AccordionSummary expandIcon={<BsChevronDown />}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                      rowGap={1}
                    >
                      <Typography fontWeight={600}>
                        {group.meta.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.meta.description}
                      </Typography>
                      {group.settings.map((setting) => (
                        <Tooltip
                          key={setting.key}
                          title={sourceMeta(setting.source).help}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${dimensionLabel(
                              parseTierKey(setting.key)?.dimension ?? ""
                            )}: ${describeLimitValue(setting.value)}`}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    {group.settings.map((setting, index) => {
                      const dimension =
                        parseTierKey(setting.key)?.dimension ?? "";

                      return (
                        <React.Fragment key={setting.key}>
                          {index > 0 && <Divider />}
                          <RateLimitSettingRow
                            setting={setting}
                            label={dimensionLabel(dimension)}
                            saving={savingKey === setting.key}
                            onSave={handleSave}
                          />
                        </React.Fragment>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* ---------------------------------------------- Kill switch confirm */}
      <Popover open={confirmDisable} handleClose={() => setConfirmDisable(false)}>
        {/*
         * `Item`, not `Box`: reactjs-popup's stylesheet is never imported, so
         * the popup wrapper is transparent — the content must bring its own
         * opaque surface or this reads straight through to the page behind it.
         */}
        <Item sx={{ p: 3, maxWidth: 480, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Turn off all rate limiting?
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Every limit stops being enforced — registration, OTP sends, code
            verification, uploads and paid third-party lookups all become
            uncapped until you turn this back on. Only do this to resolve an
            incident where limits are blocking legitimate users.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirmDisable(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setConfirmDisable(false);
                setEnabled(false);
              }}
            >
              Turn off rate limiting
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Box>
  );
};
